import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {DeviceAuthenticatorsService} from 'src/app/shared/services/device-authenticators.service';
import {AuthenticatorModel, DeviceAuthenticatorsModel} from 'src/app/shared/models/authenticator.model';
import {IdpUrlModel} from 'src/app/shared/models/idp-url.model';
import {ConfirmationModalComponent} from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';
import {BsModalService} from 'ngx-bootstrap/modal';
import {SelfManageService} from 'src/app/shared/services/self-manage.service';
import {ColumnMode, SelectionType} from '@swimlane/ngx-datatable';
import {TranslateService} from '@ngx-translate/core';
import {RedirectService} from 'src/app/shared/services/redirect.service';
import {StaticResourcesService} from 'src/app/shared/services/static-resources.service';
import {NavbarService} from 'src/app/shared/services/navbar.service';
import {AlertService} from 'src/app/shared/services/alert.service';

type LdapMethods = 'LDAP_PASSWORD_RESET' | 'LDAP_PASSWORD_RENEW';

@Component({
  selector: 'app-authenticators',
  templateUrl: './authenticators.component.html',
  styleUrls: ['./authenticators.component.scss']
})
export class AuthenticatorsComponent implements OnInit {

  isLoading: boolean = false;
  deviceAuthenticatorsList: Array<DeviceAuthenticatorsModel>;

  isEmptyAuthenticatorsList = false;
  adPasswordSelfRenewal = false;
  adPasswordSelfReset = false;
  hideRemoveAuthenticatorsButton: boolean = false;

  iconMapping = {
    SMS: {type: 'icon', icon: 'sms'},
    TOTP_DESKTOP: {type: 'icon', icon: 'clock'},
    PIN: {type: 'icon', icon: 'asterisk'},
    YUBICO_OTP: {type: 'icon', icon: ['fab', 'usb']},
    EXTERNAL_PIN: {type: 'icon', icon: 'asterisk'},
    LDAP_PASSWORD: {type: 'icon', icon: 'key'},
    FIDO: {type: 'image', src: 'assets/nucleo/fido_logo_black.png'},
    DESKTOP_FACE: {type: 'icon', icon: 'eye'},
    DESKTOP_FINGERPRINT: {type: 'icon', icon: ['far', 'hand-paper']},
    LOST_MODE: {type: 'icon', icon: 'question'},
    '4F': {type: 'icon', icon: ['far', 'hand-paper']},
    VFACE: {type: 'icon', icon: 'eye'},
    SW_OTP: {type: 'icon', icon: 'mobile', style: {paddingLeft: '7px'}},
    HW_OTP: {type: 'icon', icon: 'laptop-code'},
    ENTRA_PASSKEY: {type: 'image', src: 'assets/nucleo/key-2.png'},
    TOUCHID: {type: 'icon', icon: 'fingerprint'},
    TOTP: {type: 'icon', icon: 'clock'},
    PASSKEY: {type: 'image', src: 'assets/nucleo/key-2.png'},
    WBF : {type: 'icon', icon: 'fingerprint'},
  };
  isSynchronizing: any;

  constructor(private router: Router,
              private deviceAuthenticatorsService: DeviceAuthenticatorsService,
              private bsModalService: BsModalService,
              private selfManageService: SelfManageService,
              public staticResources: StaticResourcesService,
              public translate: TranslateService,
              public redirectService: RedirectService,
              public navbarService: NavbarService,
              public alertService: AlertService,
              private staticResourcesService: StaticResourcesService
  ) {
  }

  ngOnInit(): void {
    this.loadAuthenticators();
    this.fetchConfigs();
    this.fetchUserDetails();
  }

  fetchConfigs() {
    this.staticResourcesService.fetchConfigs().subscribe(config => {
      this.adPasswordSelfReset = config?.adPasswordSelfReset;
      this.adPasswordSelfRenewal = config?.adPasswordSelfRenewal;
      this.hideRemoveAuthenticatorsButton = config?.hideRemoveButtonAction;
    });
  }

  loadAuthenticators() {
    this.isLoading = true;
    this.deviceAuthenticatorsService.fetchDeviceAuthenticators()
      .subscribe(
        resp => {
          this.isLoading = false;
          if (resp.error.errorCode == 101) {
            this.redirectService.redirectToSAMLLogout();
          } else {
            this.deviceAuthenticatorsList = resp.deviceAuthenticators;
            this.postFetchingAuthenticators();
          }
        },
        error => {
          this.isLoading = false;
          if (error.status !== 401) {
            console.log(error);
          }
        }
      );
  }

  onBlockDevice(deviceAuth: DeviceAuthenticatorsModel) {
    var title = 'Are you sure you want to block this device?';
    this.translate.get('POP-UPS.BLOCK_DEVICE_TITLE', {value: title}).subscribe((res: string) => {
      title = res;
    });

    var body = 'This device will no longer be able to be used as authenticator.';
    this.translate.get('POP-UPS.BLOCK_DEVICE_MESSAGE', {value: body}).subscribe((res: string) => {
      body = res;
    });

    const modal = this.bsModalService.show(ConfirmationModalComponent);
    (<ConfirmationModalComponent>modal.content).showConfirmationModal(
      title,
      body
    );

    (<ConfirmationModalComponent>modal.content).onClose.subscribe(result => {

      if (result === true) {
        // when pressed Yes
        this.blockDevice(deviceAuth);

      } else if (result === false) {
        // when pressed No
      } else {
        // When closing the modal without no or yes
      }
    });
  }

  blockDevice(deviceAuth: DeviceAuthenticatorsModel) {
    this.isLoading = true;
    this.deviceAuthenticatorsService.blockDevice(deviceAuth)
      .subscribe(
        () => {
          this.isLoading = false;
          this.loadAuthenticators();
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );
  }

  onUnblockDevice(deviceAuth: DeviceAuthenticatorsModel) {
    var title = 'Are you sure you want to unblock this device?';
    this.translate.get('POP-UPS.UNBLOCK_DEVICE_TITLE', {value: title}).subscribe((res: string) => {
      title = res;
    });

    var body = 'The device will be ready to be used again as authenticator.';
    this.translate.get('POP-UPS.UNBLOCK_DEVICE_MESSAGE', {value: body}).subscribe((res: string) => {
      body = res;
    });
    const modal = this.bsModalService.show(ConfirmationModalComponent);
    (<ConfirmationModalComponent>modal.content).showConfirmationModal(
      title,
      body
    );

    (<ConfirmationModalComponent>modal.content).onClose.subscribe(result => {

      if (result === true) {
        // when pressed Yes
        this.unblockDevice(deviceAuth);

      } else if (result === false) {
        // when pressed No
      } else {
        // When closing the modal without no or yes
      }
    });
  }

  unblockDevice(deviceAuth: DeviceAuthenticatorsModel) {
    this.isLoading = true;
    this.deviceAuthenticatorsService.unblockDevice(deviceAuth)
      .subscribe(
        () => {
          this.isLoading = false;
          this.loadAuthenticators();
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );
  }

  onRemoveDevice(deviceAuth: DeviceAuthenticatorsModel) {
    var title = 'Are you sure you want to remove this device?';
    this.translate.get('POP-UPS.REMOVE_DEVICE_TITLE', {value: title}).subscribe((res: string) => {
      title = res;
    });

    var body = 'You cannot undo this action.';
    this.translate.get('POP-UPS.REMOVE_DEVICE_MESSAGE', {value: body}).subscribe((res: string) => {
      body = res;
    });

    const modal = this.bsModalService.show(ConfirmationModalComponent);
    (<ConfirmationModalComponent>modal.content).showConfirmationModal(
      title,
      body
    );

    (<ConfirmationModalComponent>modal.content).onClose.subscribe(result => {

      if (result === true) {
        // when pressed Yes
        this.removeDevice(deviceAuth);

      } else if (result === false) {
        // when pressed No
      } else {
        // When closing the modal without no or yes
      }
    });
  }

  removeDevice(deviceAuth: DeviceAuthenticatorsModel) {
    this.isLoading = true;
    this.deviceAuthenticatorsService.removeDevice(deviceAuth)
      .subscribe(
        (resp) => {
          if (resp.error.errorCode != 0) {
            var errorMessage = 'An error occurred while deleting device.';
            this.alertService.error(resp.error.errorDescription, errorMessage);
          } else {
            this.isLoading = false;
            this.loadAuthenticators();
          }
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );
  }

  onRemoveAuthenticator(onSuccess: () => void): void {
    let title = 'Are you sure you want to remove this authenticator?';
    let body = 'You cannot undo this action.';

    // Translate the title and body
    this.translate.get('POP-UPS.REMOVE_AUTH_TITLE', {value: title}).subscribe(res => title = res);
    this.translate.get('POP-UPS.REMOVE_AUTH_MESSAGE', {value: body}).subscribe(res => body = res);

    // Show confirmation modal
    const modal = this.bsModalService.show(ConfirmationModalComponent);
    const modalContent = <ConfirmationModalComponent>modal.content;
    modalContent.showConfirmationModal(title, body);

    // Handle modal closure
    modalContent.onClose.subscribe(result => {
      if (result === true) {
        // Run the callback function
        if (onSuccess) {
          onSuccess();
        }
      } else if (result === false) {
        // User canceled the action
      } else {
        // Modal closed without action
      }
    });
  }

  removeAuth(authenticator: AuthenticatorModel) {
    this.onRemoveAuthenticator(() => {
      if (authenticator.type == "ENTRA_PASSKEY") {
        this.removeEntraAuthenticator(authenticator);
      } else {
        this.removeAuthenticator(authenticator);
      }

    })
  }

  removeAuthenticator(authenticator: AuthenticatorModel) {
    this.isLoading = true;
    this.deviceAuthenticatorsService.removeAuthenticator(authenticator)
      .subscribe(
        (resp) => {
          if (resp.error.errorCode != 0) {
            var errorMessage = 'An error occurred while deleting authenticator.';
            this.alertService.error(resp.error.errorDescription, errorMessage);
          } else {
            this.isLoading = false;
            this.loadAuthenticators();
          }
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );
  }

  removeEntraAuthenticator(authenticator: AuthenticatorModel) {
    this.isLoading = true;
    this.deviceAuthenticatorsService.removeEntraAuthenticator(authenticator)
      .subscribe(
        (resp) => {
          if (resp?.error) {
            var errorMessage = 'An error occurred while deleting authenticator.';
            this.alertService.error(resp.error.errorDescription, errorMessage);
          } else {
            this.isLoading = false;
            this.loadAuthenticators();
          }
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );
  }

  onResetPin() {
    var username = this.selfManageService.getLoadedIdentity().username;
    this.router.navigate(['enrollment', username], {queryParams: {action: "RESET_PIN"}});
  }

  postFetchingAuthenticators() {
    if (!this.deviceAuthenticatorsList) {
      return;
    }
    let isEmptyAuthenticatorsList = true;
    this.deviceAuthenticatorsList.forEach(deviceAuthenticator => {
      if (deviceAuthenticator.type == "OTHERS") {
        if (deviceAuthenticator.authenticators && deviceAuthenticator.authenticators.length != 0) {
          isEmptyAuthenticatorsList = false;
        }
        deviceAuthenticator.authenticators.forEach(authenticatorModel => {
          if (authenticatorModel.type == "FIDO") {

            let str = ", " + authenticatorModel.name;

            if (authenticatorModel.details != null
              && Object.keys(authenticatorModel.details).length > 0
              && authenticatorModel.details.origin != null) {


              let fidoOrigin = authenticatorModel.details.origin;
              let configIDPs = this.staticResources.configSubjectLastValue;
              let fidoRPs: IdpUrlModel[] = configIDPs?.fidoRpList;

              if (fidoRPs === undefined || fidoRPs === null || fidoRPs.length == 0) {
                return str;
              }
              let filteredRPs = fidoRPs.filter(idpUrl => idpUrl.origins === fidoOrigin);
              if (filteredRPs.length > 0) {
                str += " (" + filteredRPs[0].name + ")";
              }
            }

            authenticatorModel.fidoCalculatedName = str;
          }
        })
      } else {
        isEmptyAuthenticatorsList = false;
      }
    })

    this.isEmptyAuthenticatorsList = isEmptyAuthenticatorsList;
  }

  onUnblockIdentity() {
    var title = 'Unblock identity';
    this.translate.get('POP-UPS.UNBLOCK_IDENTITY_TITLE', {value: title}).subscribe((res: string) => {
      title = res;
    });

    var body = 'Are you sure you want to unblock your identity?';
    this.translate.get('POP-UPS.UNBLOCK_IDENTITY_MESSAGE', {value: body}).subscribe((res: string) => {
      body = res;
    });

    const modal = this.bsModalService.show(ConfirmationModalComponent);
    (<ConfirmationModalComponent>modal.content).showConfirmationModal(
      title,
      body
    );

    (<ConfirmationModalComponent>modal.content).onClose.subscribe(result => {

      if (result === true) {
        // when pressed Yes
        this.unblockIdentity();

      } else if (result === false) {
        // when pressed No
      } else {
        // When closing the modal without no or yes
      }
    });
  }

  unblockIdentity() {
    this.selfManageService.unblock()
      .subscribe(
        resp => {
          if (resp.error.errorCode != 0) {
            var errorMessage = 'An error occurred while unblocking identity.';
            this.translate.get('ERROR.UNBLOCK_IDENTITY', {value: errorMessage}).subscribe((res: string) => {
              errorMessage = res;
            });
            this.alertService.error(resp.error.errorDescription, errorMessage);
          } else {
            window.location.reload();
          }
        },
        error => {
          console.log(error);
        }
      );
  }

  resetLdapPassword(ldapMethod: LdapMethods, row: any) {
    if (row?.status === 'EXPIRED' && ldapMethod === 'LDAP_PASSWORD_RENEW') {
      var message = 'Your password is expired! You cannot renew it, you have to reset the password!';
      this.translate.get('ENROLLMENT.LDAP_PASSWORD_RENEW_EXPIRED', {value: message}).subscribe((res: string) => {
        message = res;
      });
      this.alertService.error(message);
      return;
    }
    this.router.navigate(['enrollment', this.selfManageService.getLoadedIdentity().username], {queryParams: {action: ldapMethod}});
  }

  fetchUserDetails() {
    this.selfManageService.fetchDetails().subscribe(
      identity => {
        this.navbarService.identity = identity;
      },
      _ => {
      }
    );
  }

  onSyncEntraPasskeys(deviceId: string) {
    this.isSynchronizing = true;
    this.deviceAuthenticatorsService.syncEntraPasskeys(deviceId)
      .subscribe(response => {
          this.isSynchronizing = false;
          if (response?.error?.errorCode !== 0) {
            this.alertService.error(response.error.errorDescription);
            return
          }

          let message = 'Successfully synced passkeys';
          this.translate.get('ACTIVITY.PASSKEY_SYNC', {value: message}).subscribe((res: string) => {
            message = res;
          });
          this.alertService.success(message);
          this.loadAuthenticators();
        },
        error => {
          this.isSynchronizing = false;
          console.log(error);
        }
      );
  }
}
